import { calcularFinanzasPropiedad } from "./finanzasPropiedad";
import { calcularProrrateoParaPropiedad } from "./prorrateoCostos";
import { safeMoneyNumber } from "./contextoFinanciero";
import {
  deudaPerteneceAPropiedad,
  distribuirDeudasParaPropiedad,
  esPropiedadDeSocio,
  esPropiedadVendida,
  generaUtilidadKye,
  participaInventarioKye,
} from "./compatFinanciera";

const nombrePropiedad = (propiedad = {}) =>
  propiedad.codigoHumano ||
  propiedad.folio ||
  [propiedad.fraccNombre, propiedad.manzana && `MZ ${propiedad.manzana}`, propiedad.lote && `LT ${propiedad.lote}`]
    .filter(Boolean)
    .join(" - ") ||
  propiedad.id;

const emptyOrigen = (label) => ({
  label,
  count: 0,
  total: 0,
});

const ORIGEN_LABELS = {
  directo_lote: "Directo al lote",
  propiedad: "Heredado de propiedad/obra directa",
  obra: "Heredado de obra",
  fraccionamiento: "Heredado de fraccionamiento",
  proyecto: "Heredado de proyecto",
  no_capitalizable: "No capitalizable",
};

const addOrigen = (target, key, amount) => {
  if (!target[key]) target[key] = emptyOrigen(ORIGEN_LABELS[key] || key);
  target[key].count += 1;
  target[key].total += safeMoneyNumber(amount);
};

const construirCostosPorOrigen = (gastosDirectos = [], asignaciones = []) => {
  const origenes = {};

  gastosDirectos.forEach((gasto) => {
    const key = gasto.capitalizable === true ? "directo_lote" : "no_capitalizable";
    addOrigen(origenes, key, gasto.importe);
  });

  asignaciones.forEach((asignacion) => {
    const key = asignacion.contexto?.nivel || "prorrateado";
    addOrigen(origenes, key, asignacion.montoAsignado);
  });

  return origenes;
};

export const construirResumenLotes = ({
  propiedades = [],
  deudas = [],
  gastos = [],
  catalogos = {},
} = {}) => {
  const propiedadesActivas = propiedades.filter((p) => p.activo !== false);

  const rows = propiedadesActivas.map((propiedad) => {
    const deudasPropiedadBase = deudas.filter((deuda) =>
      deudaPerteneceAPropiedad(deuda, propiedad.id)
    );
    const deudasPropiedad = distribuirDeudasParaPropiedad(
      deudasPropiedadBase,
      propiedad,
      propiedadesActivas
    );
    const gastosDirectos = gastos.filter((gasto) => gasto.propiedadId === propiedad.id);
    const prorrateo = calcularProrrateoParaPropiedad({
      propiedad,
      gastos,
      propiedades,
      catalogos,
    });
    const finanzas = calcularFinanzasPropiedad({
      propiedad,
      deudas: deudasPropiedad,
      gastosDirectos,
      costosProrrateados: prorrateo.totalProrrateado,
    });
    const costosPorOrigen = construirCostosPorOrigen(
      gastosDirectos,
      prorrateo.asignaciones
    );

    return {
      id: propiedad.id,
      nombre: nombrePropiedad(propiedad),
      folio: propiedad.folio || "",
      estado: propiedad.estado || "",
      proyectoId: propiedad.proyectoId || "",
      proyectoNombre: propiedad.proyectoNombre || propiedad.proyectoId || "",
      fraccionamientoId: propiedad.fraccionamientoId || "",
      fraccionamientoNombre: propiedad.fraccNombre || propiedad.fraccionamientoNombre || propiedad.fraccionamientoId || "",
      manzana: propiedad.manzana || "",
      lote: propiedad.lote || "",
      metros2: safeMoneyNumber(propiedad.metros2),
      vendido: esPropiedadVendida(propiedad),
      propiedadSocio: esPropiedadDeSocio(propiedad),
      generaUtilidadKye: generaUtilidadKye(propiedad),
      participaInventarioKye: participaInventarioKye(propiedad),
      contratoMultipropiedad: deudasPropiedad.some((deuda) => deuda.contratoMultipropiedad),
      factorDistribucion: deudasPropiedad[0]?.factorDistribucion ?? 1,
      deudaId: finanzas.deudaPrincipal?.id || "",
      clienteId: finanzas.deudaPrincipal?.clienteId || "",
      clienteNombre: finanzas.deudaPrincipal?.clienteNombre || "",
      ingresoVendido: finanzas.ingresoVendido,
      ingresoProyectado: finanzas.ingresoProyectado,
      totalCobrado: finanzas.totalCobrado,
      saldoPendiente: finanzas.saldoPendiente,
      costosDirectos: finanzas.costosDirectos,
      costosDirectosCapitalizables: finanzas.costosDirectosCapitalizables,
      costosDirectosNoCapitalizables: finanzas.costosDirectosNoCapitalizables,
      costosProrrateados: finanzas.costosProrrateados,
      costoTotalAsignado: finanzas.costoTotalAsignado,
      utilidadProyectada: finanzas.utilidadProyectadaConProrrateo,
      utilidadRealizada: finanzas.utilidadRealizadaConProrrateo,
      margen: finanzas.margenConProrrateo,
      gastosDirectosCount: finanzas.gastosDirectosCount,
      prorrateosCount: prorrateo.asignaciones.length,
      costosPorOrigen,
    };
  });

  rows.sort((a, b) => safeMoneyNumber(b.utilidadProyectada) - safeMoneyNumber(a.utilidadProyectada));

  const resumen = rows.reduce(
    (acc, row) => {
      acc.lotes += 1;
      acc.vendidos += row.vendido ? 1 : 0;
      acc.ingresoVendido += row.ingresoVendido;
      acc.ingresoProyectado += row.ingresoProyectado;
      acc.totalCobrado += row.totalCobrado;
      acc.saldoPendiente += row.saldoPendiente;
      acc.costoTotalAsignado += row.costoTotalAsignado;
      acc.utilidadProyectada += row.utilidadProyectada;
      acc.utilidadRealizada += row.utilidadRealizada;
      return acc;
    },
    {
      lotes: 0,
      vendidos: 0,
      ingresoVendido: 0,
      ingresoProyectado: 0,
      totalCobrado: 0,
      saldoPendiente: 0,
      costoTotalAsignado: 0,
      utilidadProyectada: 0,
      utilidadRealizada: 0,
      margen: null,
    }
  );

  resumen.margen =
    resumen.ingresoProyectado > 0 ? resumen.utilidadProyectada / resumen.ingresoProyectado : null;

  return { resumen, rows };
};
